import React, { useEffect, useRef, useState } from "react";

/**
 * SecureSlidePlayer
 * - Loads a Google Slides /pubembed URL with no toolbar
 * - Adds a drawing canvas overlay
 * - Fullscreen: uses native Fullscreen API when allowed; otherwise falls back to CSS fullscreen
 *
 * Props:
 * - embedSrc (string): Google Slides /pubembed URL
 * - aspectRatio (number): default 16/9
 * - allowFullscreen (boolean): default true (button hides when false)
 */
export default function SecureSlidePlayer({
  embedSrc,
  aspectRatio = 16 / 9,
  allowFullscreen = true,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  const [drawMode, setDrawMode] = useState(false);
  const [brushSize, setBrushSize] = useState(4);
  const [brushColor, setBrushColor] = useState("#ff5252");
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState(null);

  // Fallback “CSS fullscreen” when Fullscreen API is blocked by Permissions Policy
  const [useCssFullscreen, setUseCssFullscreen] = useState(false);

  // Is the native Fullscreen API available & allowed?
  const [fullscreenSupported, setFullscreenSupported] = useState(false);

  // NEW: toolbar visibility
  const [toolbarVisible, setToolbarVisible] = useState(true);

  useEffect(() => {
    setFullscreenSupported(Boolean(document.fullscreenEnabled));
  }, []);

  // Resize canvas to match container’s CSS box (and DPR)
  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!canvas || !rect) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = (rect.width / aspectRatio) * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.width / aspectRatio}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
  };

  useEffect(() => {
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
    // eslint-disable-next-line
  }, [aspectRatio, useCssFullscreen]);

  // Drawing helpers
  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    if (!drawMode) return;
    setIsDrawing(true);
    setLastPoint(getPos(e));
  };

  const moveDraw = (e) => {
    if (!drawMode || !isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const current = getPos(e);
    ctx.strokeStyle = brushColor;
    ctx.lineWidth = brushSize;
    ctx.beginPath();
    ctx.moveTo(lastPoint.x, lastPoint.y);
    ctx.lineTo(current.x, current.y);
    ctx.stroke();
    setLastPoint(current);
  };

  const endDraw = () => {
    setIsDrawing(false);
    setLastPoint(null);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // Fullscreen handling
  const enterCssFullscreen = () => setUseCssFullscreen(true);
  const exitCssFullscreen = () => setUseCssFullscreen(false);

  const toggleFullscreen = async () => {
    if (!allowFullscreen) return;

    if (!fullscreenSupported) {
      setUseCssFullscreen((v) => !v);
      return;
    }

    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (err) {
      setUseCssFullscreen((v) => !v);
    }
  };

  // Close CSS fullscreen with ESC
  useEffect(() => {
    const onKey = (e) => {
      if (useCssFullscreen && e.key === "Escape") exitCssFullscreen();
      if (e.key.toLowerCase() === "t") setToolbarVisible((v) => !v); // toggle toolbar via keyboard
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [useCssFullscreen]);

  // Container styles (normal vs CSS fullscreen)
  const containerStyle = useCssFullscreen
    ? {
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(0,0,0,0.92)",
        margin: 0,
        width: "100vw",
        height: "100vh",
        userSelect: "none",
      }
    : {
        width: "100%",
        maxWidth: 1200,
        margin: "0 auto",
        position: "relative",
        aspectRatio: "16 / 9",
        userSelect: "none",
      };

  const toolbarBaseStyle = {
    position: "absolute",
    top: 8,
    left: 8,
    zIndex: 6,
    display: "flex",
    gap: 8,
    background: "rgba(18,18,18,0.7)",
    padding: 8,
    borderRadius: 12,
    backdropFilter: "blur(6px)",
  };

  const toolButtonStyle = {
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(0,0,0,0.4)",
    color: "#fff",
    cursor: "pointer",
  };

  return (
    <div
      ref={containerRef}
      onContextMenu={(e) => e.preventDefault()}
      style={containerStyle}
    >
      {/* Toolbar (hideable) */}
      {toolbarVisible && (
        <div style={toolbarBaseStyle}>
          <button
            onClick={() => setDrawMode((v) => !v)}
            style={{
              ...toolButtonStyle,
              background: drawMode
                ? "rgba(255,255,255,0.15)"
                : "rgba(0,0,0,0.4)",
            }}
            title="Toggle drawing"
          >
            ✏️ {drawMode ? "Drawing On" : "Drawing Off"}
          </button>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "#fff",
            }}
            title="Brush size"
          >
            Size
            <input
              type="range"
              min={2}
              max={18}
              value={brushSize}
              onChange={(e) => setBrushSize(parseInt(e.target.value, 10))}
            />
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "#fff",
            }}
            title="Brush color"
          >
            Color
            <input
              type="color"
              value={brushColor}
              onChange={(e) => setBrushColor(e.target.value)}
              style={{
                width: 36,
                height: 28,
                border: "none",
                background: "transparent",
              }}
            />
          </label>

          <button
            onClick={clearCanvas}
            style={toolButtonStyle}
            title="Clear annotations"
          >
            🧽 Clear
          </button>

          {allowFullscreen && (
            <button
              onClick={toggleFullscreen}
              style={toolButtonStyle}
              title={fullscreenSupported ? "Fullscreen" : "Expand"}
            >
              {useCssFullscreen || document.fullscreenElement
                ? "⤢ Exit"
                : fullscreenSupported
                ? "⛶ Fullscreen"
                : "⤢ Expand"}
            </button>
          )}

          {/* NEW: Hide toolbar */}
          <button
            onClick={() => setToolbarVisible(false)}
            style={toolButtonStyle}
            title="Hide toolbar (press T to toggle)"
          >
            Hide
          </button>
        </div>
      )}

      {/* NEW: Minimal "Tools" tab when toolbar is hidden */}
      {!toolbarVisible && (
        <button
          onClick={() => setToolbarVisible(true)}
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            zIndex: 7,
            padding: "6px 10px",
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.2)",
            background: "rgba(18,18,18,0.6)",
            color: "#fff",
            fontSize: 12,
            cursor: "pointer",
            backdropFilter: "blur(6px)",
          }}
          title="Show toolbar (T)"
        >
          🛠️ Tools
        </button>
      )}

      {/* Google Slides player */}
      <iframe
        src={embedSrc}
        title="Presentation"
        allowFullScreen
        allow="fullscreen; encrypted-media; picture-in-picture; clipboard-write"
        sandbox="allow-scripts allow-same-origin allow-presentation"
        referrerPolicy="no-referrer"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          border: 0,
          pointerEvents: drawMode ? "none" : "auto",
          background: "black",
        }}
      />

      {/* Click-block shim for any pop-out UI in top-right (best-effort only) */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 140,
          height: 80,
          zIndex: 4,
          pointerEvents: "auto",
        }}
      />

      {/* Drawing overlay */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 3,
          pointerEvents: drawMode ? "auto" : "none",
          touchAction: "none",
        }}
        onMouseDown={startDraw}
        onMouseMove={moveDraw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={moveDraw}
        onTouchEnd={endDraw}
      />

      {/* Hint */}
      <div
        style={{
          position: "absolute",
          bottom: 35,
          left: 8,
          zIndex: 5,
          padding: "6px 10px",
          borderRadius: 8,
          fontSize: 12,
          color: "#ddd",
          background: "rgba(0,0,0,0.4)",
        }}
      >
        Tip: Press <b>T</b> to toggle the toolbar. Turn drawing OFF to use slide
        arrows & keyboard.
      </div>
    </div>
  );
}
